import { makeObservable, observable, action, computed, reaction, runInAction } from 'mobx';
import type { RootStore } from '.';
import { db } from '../firebase-modules';

export interface Milestone {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  type:
    | 'review'
    | 'deadline'
    | 'approval'
    | 'qc'
    | 'retrospective'
    | 'delivery'
    | 'launch'
    | 'planning'
    | 'production'
    | 'other';
  customType?: string;
  description?: string;
  ownedBy?: string[];
  informed?: string[];
  isCompleted: boolean;
  completedAt?: number;
  createdAt: number;
  updatedAt: number;
  status: 'todo' | 'inProgress' | 'done';
}

export class UiProjectMilestonesStore {
  private rootStore: RootStore;
  private isLoading = false;
  private unsubscribe: (() => void) | null = null;
  milestones: Record<string, Milestone> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<UiProjectMilestonesStore, 'milestones' | 'isLoading'>(this, {
      milestones: observable,
      isLoading: observable,
      sortedMilestones: computed,
      addMilestone: action,
      updateMilestone: action,
      deleteMilestone: action,
      toggleMilestoneCompletion: action,
      clearMilestones: action,
    });

    // Subscribe to milestone updates when session changes
    reaction(
      () => this.rootStore.sessionsStore.currentSession?.id,
      (sessionId) => {
        this.cleanup();
        if (sessionId) {
          this.subscribeMilestones(sessionId);
        } else {
          this.clearMilestones();
        }
      },
    );
  }

  private cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  private subscribeMilestones(sessionId: string) {
    this.isLoading = true;

    // Set up real-time listener
    this.unsubscribe = db
      .collection('sessions')
      .doc(sessionId)
      .onSnapshot(
        (doc) => {
          const data = doc.data();
          runInAction(() => {
            if (data?.milestones) {
              this.milestones = data.milestones;
            } else {
              this.milestones = {};
            }
            this.isLoading = false;
          });
        },
        (error) => {
          console.error('Error subscribing to milestones:', error);
          runInAction(() => {
            this.milestones = {};
            this.isLoading = false;
          });
        },
      );
  }

  get sortedMilestones(): Milestone[] {
    return Object.values(this.milestones).sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
  }

  get loading(): boolean {
    return this.isLoading;
  }

  async addMilestone(milestone: Omit<Milestone, 'id' | 'isCompleted' | 'createdAt' | 'updatedAt'>) {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session) return;

    const id = Date.now().toString();
    const now = Date.now();

    // Create milestone with only defined values
    const newMilestone: Milestone = {
      id,
      title: milestone.title,
      startDate: milestone.startDate,
      endDate: milestone.endDate,
      type: milestone.type,
      isCompleted: false,
      createdAt: now,
      updatedAt: now,
      status: milestone.status || 'todo',
    };

    // Only add optional fields if they are defined and not empty
    if (milestone.customType) newMilestone.customType = milestone.customType;
    if (milestone.description) newMilestone.description = milestone.description;
    if (milestone.ownedBy?.length) newMilestone.ownedBy = [...milestone.ownedBy];
    if (milestone.informed?.length) newMilestone.informed = [...milestone.informed];

    try {
      // Create a copy of current milestones and add the new one
      const updatedMilestones = {
        ...this.milestones,
        [id]: newMilestone,
      };

      // Update Firestore
      await db.collection('sessions').doc(session.id).update({
        milestones: updatedMilestones,
      });
    } catch (error) {
      console.error('Error adding milestone:', error);
      throw error;
    }
  }

  async updateMilestone(id: string, updates: Partial<Omit<Milestone, 'id' | 'createdAt'>>) {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session || !this.milestones[id]) return;

    try {
      const currentMilestone = this.milestones[id];
      const updatedMilestone: Milestone = {
        ...currentMilestone,
        updatedAt: Date.now(),
      };

      // Only update fields that are defined and not empty
      if ('title' in updates) updatedMilestone.title = updates.title!;
      if ('startDate' in updates) updatedMilestone.startDate = updates.startDate!;
      if ('endDate' in updates) updatedMilestone.endDate = updates.endDate!;
      if ('type' in updates) updatedMilestone.type = updates.type!;

      // Handle status and completion state together
      if ('status' in updates) {
        const newStatus = updates.status as 'todo' | 'inProgress' | 'done';
        updatedMilestone.status = newStatus;
        // If moving to done, mark as completed
        if (newStatus === 'done') {
          updatedMilestone.isCompleted = true;
          updatedMilestone.completedAt = Date.now();
        }
        // If moving out of done, remove completion state
        else {
          updatedMilestone.isCompleted = false;
          delete updatedMilestone.completedAt;
        }
      }
      // Only update completion state directly if status isn't being changed
      else if ('isCompleted' in updates) {
        updatedMilestone.isCompleted = updates.isCompleted!;
        if (updates.isCompleted) {
          updatedMilestone.status = 'done';
          updatedMilestone.completedAt = Date.now();
        }
      }

      // Handle optional fields
      if ('customType' in updates) {
        if (updates.customType) {
          updatedMilestone.customType = updates.customType;
        } else {
          delete updatedMilestone.customType;
        }
      }
      if ('description' in updates) {
        if (updates.description) {
          updatedMilestone.description = updates.description;
        } else {
          delete updatedMilestone.description;
        }
      }
      if ('ownedBy' in updates) {
        if (updates.ownedBy?.length) {
          updatedMilestone.ownedBy = [...updates.ownedBy];
        } else {
          delete updatedMilestone.ownedBy;
        }
      }
      if ('informed' in updates) {
        if (updates.informed?.length) {
          updatedMilestone.informed = [...updates.informed];
        } else {
          delete updatedMilestone.informed;
        }
      }

      // Create a copy of current milestones and update the specific one
      const updatedMilestones = {
        ...this.milestones,
        [id]: updatedMilestone,
      };

      // Update Firestore
      await db.collection('sessions').doc(session.id).update({
        milestones: updatedMilestones,
      });
    } catch (error) {
      console.error('Error updating milestone:', error);
      throw error;
    }
  }

  async toggleMilestoneCompletion(id: string) {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session || !this.milestones[id]) return;

    try {
      const now = Date.now();
      const isCompleting = !this.milestones[id].isCompleted;
      const updatedMilestone = {
        ...this.milestones[id],
        isCompleted: isCompleting,
        status: isCompleting ? 'done' : 'todo',
        updatedAt: now,
      };

      // Add completedAt when completing, remove it when uncompleting
      if (isCompleting) {
        updatedMilestone.completedAt = now;
      } else {
        delete updatedMilestone.completedAt;
      }

      // Create a copy of current milestones and update the specific one
      const updatedMilestones = {
        ...this.milestones,
        [id]: updatedMilestone,
      };

      // Update Firestore
      await db.collection('sessions').doc(session.id).update({
        milestones: updatedMilestones,
      });
    } catch (error) {
      console.error('Error toggling milestone completion:', error);
      throw error;
    }
  }

  async deleteMilestone(id: string) {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session || !this.milestones[id]) return;

    try {
      // Create a copy of current milestones and remove the specific one
      const updatedMilestones = { ...this.milestones };
      delete updatedMilestones[id];

      // Update Firestore
      await db.collection('sessions').doc(session.id).update({
        milestones: updatedMilestones,
      });
    } catch (error) {
      console.error('Error deleting milestone:', error);
      throw error;
    }
  }

  clearMilestones() {
    this.milestones = {};
  }
}
